import React from "react";
import { useShallow } from "zustand/react/shallow";
import { useOrgContextStore } from "@/components/main/stores/orgContextStore";
import { formatDayMonth } from "@/lib/dates";
import { spentLimits, type MeteredLimit } from "@/lib/api/organizations";

const NAMES: Record<MeteredLimit, string> = {
  storage: "Storage",
  egress: "Egress",
  mcp_calls: "MCP tool calls",
};

/// "Egress and MCP tool calls reset on 10 October." — the monthly allowances
/// share a reset day. `null` when only storage is spent: waiting never frees it.
function resetLine(spent: MeteredLimit[], resetsOn: string): string | null {
  const monthly = spent.filter((l) => l !== "storage").map((l) => NAMES[l]);
  if (monthly.length === 0) return null;
  const verb = monthly.length === 1 ? "resets" : "reset";
  return `${monthly.join(" and ")} ${verb} on ${resetsOn}.`;
}

/// What a spent allowance stops the whole org doing, for every member (D59):
/// one who cannot fix it still needs to know why their export failed, and is
/// told who can act. Spent state only — a warning that sits for days becomes
/// wallpaper; the 80% warning is the rail's. Not dismissible, like
/// `OrgDeletionBanner`. States the fact and names the door; is not a door.
export const QuotaBanner: React.FC = () => {
  const { org, usage } = useOrgContextStore(
    useShallow((s) => ({
      org: s.orgs.find((o) => o.org_id === s.activeOrgId),
      usage: s.usage,
    })),
  );

  const spent = spentLimits(usage);
  if (!usage || spent.length === 0) return null;

  // The deletion banner sits directly above and is louder.
  if (org?.deletion_scheduled_at) return null;

  // Buying a plan is `can_manage_billing`, an owner's alone: an admin can read
  // the meters on Plan & usage but would find Upgrade disabled there.
  const isOwner = org?.role === "owner";
  const resetsOn = formatDayMonth(usage.egress.resets_at * 1000);

  const cta = isOwner
    ? "See Plan & usage in organization settings."
    : "An owner can move to a larger plan.";
  const reset = resetLine(spent, resetsOn);

  // One shape for every combination: a label, the names, when they come back.
  return (
    <div className="org-deletion-banner" role="alert">
      <div className="banner-dot" />
      <span>
        <span className="font-medium">
          {spent.length === 1 ? "Limit reached:" : "Limits reached:"}
        </span>{" "}
        {spent.map((l) => NAMES[l]).join(", ")}.{reset ? ` ${reset}` : ""} {cta}
      </span>
    </div>
  );
};
